import { useWLOrgConfigContext } from '@src/providers/WLOrgConfigProvider';
import type { PropsWithChildren } from 'react';
import { Provider } from 'react-redux';
import { useBalanceTransactionsContext, useCurrenciesContext, useSecuritiesContext } from '../../contexts';
import { useConstant, useCurrency, useIntl } from '../../hooks';
import type { OrderStrategy, Security } from '../../types';
import { EMPTY_ARRAY } from '../../utils';
import { useSocketClient } from '../WebSocketClientProvider';
import { useWLCustomerMarketAccountContext } from '../WLCustomerMarketAccountsProvider';
import { useWLCustomerUserConfigContext, useWLHomeCurrency } from '../WLCustomerUserConfigContextProvider';
import { useWLOrdersProvider } from '../WLOrdersProvider';
import { getWLOrderFormStore } from './WLOrderFormStore';

export const WLOrderFormProvider = ({
  children,
  getStrategiesBySymbol,
}: PropsWithChildren<{ getStrategiesBySymbol: (s: Security) => OrderStrategy[] }>) => {
  const { config } = useWLCustomerUserConfigContext();
  const client = useSocketClient();
  const intl = useIntl();
  const { sessionOrdersObs } = useWLOrdersProvider();
  const { sessionTransactionsObs } = useBalanceTransactionsContext();
  const { config: orgConfig } = useWLOrgConfigContext();
  const favoredPairCurrency = orgConfig.favoredPairCurrency;
  const favoredPairCurrencyObject = useCurrency(favoredPairCurrency);
  const { homeCurrencyObject } = useWLHomeCurrency();

  const { securitiesBySymbol, securitiesList } = useSecuritiesContext();
  const { customerMarketAccountsList = EMPTY_ARRAY, customerMarketAccountsBySourceAccountID = new Map() } =
    useWLCustomerMarketAccountContext();
  const currencies = useCurrenciesContext();

  const store = useConstant(
    getWLOrderFormStore(
      {
        securities: { securitiesBySymbol, securitiesList },
        symbol: config.symbol,
        strategiesBySymbol: new Map(securitiesList.map(s => [s.Symbol, getStrategiesBySymbol(s) ?? []])),
        marketAccounts: { customerMarketAccountsList, customerMarketAccountsBySourceAccountID },
        currencies,
        favoredPairCurrency: favoredPairCurrencyObject,
        homeCurrency: homeCurrencyObject,
      },
      { wsClient: client, sessionOrdersObs, sessionTransactionsObs: sessionTransactionsObs },
      intl
    )
  );

  return <Provider store={store}>{children}</Provider>;
};
